import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getConsumerSharedUiData,
  updateAndRefreshSharedUiData
} from 'store/consumerPreferences/actions';
import {
  consumerUiDataIsFetchingSelector,
  sharedUiDataSelector
} from 'store/consumerPreferences/selectors';
import { profileConsumerSelector } from 'store/profile/selectors';
import { Consumer } from 'store/profile/types';
import analyticsService, { AnalyticsEvent } from '../../services/AnalyticsService';
import { showEmailAlert } from '../dashboard/showEmailAlert';

export function useShowEmailAlert(): [
  boolean,
  (action: 'closeDialog' | 'closeX' | 'closeBtn' | 'update') => void
] {
  const dispatch = useDispatch();
  const [openEmailAlert, setOpenEmailAlert] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  const profile: Consumer = useSelector(profileConsumerSelector);
  const sharedUiData = useSelector(sharedUiDataSelector);
  const uiDataFetching = useSelector(consumerUiDataIsFetchingSelector);

  useEffect(() => {
    if (sharedUiData) {
      setHasFetched(true);
    } else if (profile && !hasFetched && !sharedUiData && !uiDataFetching) {
      dispatch(getConsumerSharedUiData(profile.consumerId));
      setHasFetched(true);
    }
  }, [hasFetched, sharedUiData, uiDataFetching]);

  useEffect(() => {
    if (
      !hasShown && // Don't Show twice
      hasFetched && // Don't show before fetching uiData. Needed because UIData could be null
      !uiDataFetching && // Don't show while fetching uiData
      !!profile && // Don't show without profile
      showEmailAlert(profile, sharedUiData) // Show only if email is invalid and not shown in last 24 hours
    ) {
      setOpenEmailAlert(true);
    }
  }, [hasFetched, profile, uiDataFetching, sharedUiData, hasShown]);

  const handleEmailAlertClose = async (
    action: 'closeDialog' | 'closeX' | 'closeBtn' | 'update'
  ) => {
    const timesShown = sharedUiData?.timesShownUpdateEmail || 0;
    const timesShownUpdateEmail = timesShown + 1;
    setHasShown(true);
    dispatch(
      updateAndRefreshSharedUiData({
        lastSeenUpdateEmail: Date.now(),
        timesShownUpdateEmail
      })
    );

    setOpenEmailAlert(false);

    // Handle analytics
    switch (action) {
      case 'closeX':
        analyticsService.logEvent(AnalyticsEvent.InvalidEmailDialogClosedWithX, {
          timesShownUpdateEmail
        });
        break;
      case 'update':
        analyticsService.logEvent(AnalyticsEvent.InvalidEmailDialogClosedWithUpdate, {
          timesShownUpdateEmail
        });
        break;
      case 'closeBtn':
        analyticsService.logEvent(AnalyticsEvent.InvalidEmailDialogClosedWithClose, {
          timesShownUpdateEmail
        });
        break;
      case 'closeDialog':
      default:
        analyticsService.logEvent(AnalyticsEvent.InvalidEmailDialogClosedWithDialog, {
          timesShownUpdateEmail
        });
        break;
    }
  };

  return [openEmailAlert, handleEmailAlertClose];
}
